import React, { useState, useRef, useEffect } from 'react';
import { TrendingUp, Activity, Clock, Minus } from 'lucide-react';

const Stage3Dashboard = () => {
  const [stage1Files, setStage1Files] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [anchorDate, setAnchorDate] = useState('');
  const [anchorTime, setAnchorTime] = useState('10:30:00');
  const [results, setResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [moduleProgress, setModuleProgress] = useState({ current: 0, total: 0 });
  const [tickerProgress, setTickerProgress] = useState({ current: 0, total: 0 });
  const [outputFiles, setOutputFiles] = useState([]);
  const wsRef = useRef(null);

  // Load available Stage 1 files on mount
  useEffect(() => {
    loadStage1Files();
  }, []);

  const loadStage1Files = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/stage3/stage1-results`,
        {
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY
          }
        }
      );
      
      console.log('Stage 1 files response status:', response.status);
      
      if (!response.ok) {
        console.error('Failed to load Stage 1 files:', response.statusText);
        return;
      }
      
      const data = await response.json();
      console.log('Stage 1 files data:', data);
      
      setStage1Files(data.files || []);
      
      // Auto-select most recent file
      if (data.files && data.files.length > 0) {
        setSelectedFile(data.files[0].filename);
      }
    } catch (error) {
      console.error('Error loading Stage 1 files:', error);
    }
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  const getDecisionColor = (decision) => {
    if (!decision) return 'gray';
    const d = decision.toLowerCase();
    if (d.includes('buy') || d.includes('strong buy')) return 'green';
    if (d.includes('hold') || d.includes('watch') || d.includes('neutral')) return 'yellow';
    return 'gray';
  };

  const getDecisionIcon = (decision) => {
    if (!decision) return <Minus className="w-4 h-4" />;
    const d = decision.toLowerCase();
    if (d.includes('buy')) return <TrendingUp className="w-4 h-4" />;
    if (d.includes('hold') || d.includes('watch')) return <Activity className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const handleRunAnalysis = () => {
    if (!selectedFile) {
      alert('Please select a Stage 1 result file');
      return;
    }

    // Clear previous results
    setResults([]);
    setOutputFiles([]);
    setStatusMessage('');
    setModuleProgress({ current: 0, total: 0 });
    setTickerProgress({ current: 0, total: 0 });
    setIsRunning(true);

    // Create WebSocket connection
    const websocket = new WebSocket('wss://daytrader.thecreativecoefficient.com.au/ws/stage3/run');
    wsRef.current = websocket;
    
    websocket.onopen = () => {
      console.log('WebSocket connected');
      // Send Stage 1 file, anchor timestamp, and API key
      websocket.send(JSON.stringify({
        stage1_file: selectedFile,
        anchor: anchorDate && anchorTime ? `${anchorDate} ${anchorTime}` : null,
        api_key: process.env.REACT_APP_API_KEY
      }));
    };

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log('Received message:', message);

      switch (message.type) {
        case 'status':
          setStatusMessage(message.message);
          break;

        case 'module_start':
          setModuleProgress({ 
            current: message.current, 
            total: message.total 
          });
          setStatusMessage(`Running ${message.module}...`);
          break;

        case 'module_complete':
          console.log(`Module complete: ${message.module}`);
          break;

        case 'ticker_complete':
          // Add ticker result as it completes
          const tickerData = message.data;
          console.log('Ticker complete:', tickerData);
          
          setResults(prev => {
            // Check if ticker already exists, update it
            const existingIndex = prev.findIndex(r => r.ticker === tickerData.ticker);
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                score: tickerData.score,
                decision: tickerData.decision
              };
              return updated;
            } else {
              // Add new ticker
              return [...prev, {
                ticker: tickerData.ticker,
                score: tickerData.score,
                decision: tickerData.decision
              }];
            }
          });
          
          setTickerProgress({
            current: tickerData.current,
            total: tickerData.total
          });
          break;

        case 'info':
          // Capture output file paths
          if (message.message && (message.message.includes('📄') || message.message.includes('📊'))) {
            setOutputFiles(prev => [...prev, message.message]);
          }
          break;

        case 'complete':
          console.log('Stage 3 complete!');
          setStatusMessage('');
          setIsRunning(false);
          // Load full results from API
          loadFullResults();
          break;

        case 'error':
          console.error('Stage 3 error:', message.message);
          alert(`Error: ${message.message}`);
          setStatusMessage('');
          setIsRunning(false);
          websocket.close();
          break;

        case 'ping':
          // Keepalive ping, no action needed
          break;

        default:
          console.log('Unknown message type:', message.type);
          break;
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      setStatusMessage('Connection error');
      setIsRunning(false);
    };

    websocket.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      setIsRunning(false);
      setStatusMessage('');
    };
  };

  const loadFullResults = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/stage3/results`,
        {
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY
          }
        }
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        setResults(data.results);
      }
    } catch (error) {
      console.error('Error loading full results:', error);
    }
  };

  const handleReset = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    setResults([]);
    setOutputFiles([]);
    setStatusMessage('');
    setModuleProgress({ current: 0, total: 0 });
    setTickerProgress({ current: 0, total: 0 });
    setIsRunning(false);
  };

  // Calculate stats
  const stats = {
    total: results.length,
    buy: results.filter(r => r.decision && r.decision.toLowerCase().includes('buy')).length,
    hold: results.filter(r => r.decision && (r.decision.toLowerCase().includes('hold') || r.decision.toLowerCase().includes('watch') || r.decision.toLowerCase().includes('neutral'))).length,
    pass: results.filter(r => r.decision && r.decision.toLowerCase().includes('pass')).length
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Stage 3: Technical Analysis</h1>
              <p className="text-gray-600 mt-1">Deep analysis with M1-M13 modules + AI decisions</p>
            </div>
          </div>

          {/* Analysis Controls */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-purple-600" />
              <h3 className="text-sm font-semibold text-purple-900">Run Analysis</h3>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleRunAnalysis(); }} className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-purple-700 mb-1">
                  Select Stage 1 Results
                </label>
                <select
                  value={selectedFile}
                  onChange={(e) => setSelectedFile(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isRunning}
                  required
                >
                  <option value="">-- Select a file --</option>
                  {stage1Files.map((file) => (
                    <option key={file.filename} value={file.filename}>
                      {file.timestamp} ({file.ticker_count} tickers)
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1">
                <label className="block text-xs font-medium text-purple-700 mb-1">
                  Backtest Date (Optional)
                </label>
                <input
                  type="date"
                  value={anchorDate}
                  onChange={(e) => setAnchorDate(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isRunning}
                />
              </div>
              
              <div className="flex-1">
                <label className="block text-xs font-medium text-purple-700 mb-1">
                  Backtest Time (EST)
                </label>
                <input
                  type="time"
                  value={anchorTime}
                  onChange={(e) => setAnchorTime(e.target.value)}
                  step="60"
                  className="w-full px-3 py-2 border border-purple-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isRunning}
                />
              </div>
              
              <button
                type="submit"
                disabled={isRunning}
                className={`px-6 py-2 font-medium rounded-md transition-colors ${
                  isRunning
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {isRunning ? 'Running...' : 'Run Analysis'}
              </button>
              
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-md transition-colors"
              >
                Clear
              </button>
            </form>

            {/* Status Message */}
            {statusMessage && (
              <div className="mt-3 flex items-center gap-2 text-purple-700">
                <Activity className="w-4 h-4 animate-pulse" />
                <span className="text-sm font-medium">{statusMessage}</span>
              </div>
            )}

            {/* Module Progress */}
            {moduleProgress.total > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-purple-600 mb-1">
                  <span>Module Progress</span>
                  <span>{moduleProgress.current} / {moduleProgress.total}</span>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(moduleProgress.current / moduleProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Ticker Progress */}
            {tickerProgress.total > 0 && (
              <div className="mt-2">
                <div className="flex justify-between text-xs text-purple-600 mb-1">
                  <span>M13 Analysis</span>
                  <span>{tickerProgress.current} / {tickerProgress.total} tickers</span>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(tickerProgress.current / tickerProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium">Shortlisted Tickers</div>
              <div className="text-2xl font-bold text-blue-900 mt-1">{stats.total}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium">Buy Signals</div>
              <div className="text-2xl font-bold text-green-900 mt-1">{stats.buy}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-sm text-yellow-600 font-medium">Hold/Watch</div>
              <div className="text-2xl font-bold text-yellow-900 mt-1">{stats.hold}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 font-medium">Pass</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{stats.pass}</div>
            </div>
          </div>

          {/* Output Files */}
          {outputFiles.length > 0 && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm font-semibold text-green-900 mb-2">Generated Files:</div>
              <div className="space-y-1">
                {outputFiles.map((file, idx) => (
                  <div key={idx} className="text-sm text-green-700 font-mono">
                    {file}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Table */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ticker
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Decision
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conviction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Anchor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stop
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shares
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Risk $
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <div className="text-lg font-medium">No results yet</div>
                      <div className="text-sm mt-1">Select a Stage 1 file and click "Run Analysis"</div>
                    </td>
                  </tr>
                ) : (
                  results.map((result, idx) => {
                    const color = getDecisionColor(result.decision);
                    const colorClasses = {
                      green: 'bg-green-50 border-l-4 border-green-500',
                      yellow: 'bg-yellow-50 border-l-4 border-yellow-500',
                      gray: 'bg-gray-50 border-l-4 border-gray-400'
                    };

                    return (
                      <tr 
                        key={`${result.ticker}-${idx}`}
                        className={`hover:bg-gray-50 transition-colors animate-fadeIn ${colorClasses[color]}`}
                      >
                        {/* Ticker */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">{result.ticker}</div>
                        </td>
                        
                        {/* Decision */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`flex items-center gap-2 text-sm font-bold ${
                            color === 'green' ? 'text-green-700' :
                            color === 'yellow' ? 'text-yellow-700' :
                            'text-gray-600'
                          }`}>
                            {getDecisionIcon(result.decision)}
                            {result.decision}
                          </div>
                        </td>
                        
                        {/* Score */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">
                            {result.score}/100
                          </div>
                        </td>
                        
                        {/* Conviction */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            {result.conviction || '-'}
                          </div>
                        </td>
                        
                        {/* Anchor */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-blue-600">
                            {result.anchor_price ? `$${formatNumber(result.anchor_price)}` : '-'}
                          </div>
                        </td>
                        
                        {/* Entry */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {result.entry_price ? `$${formatNumber(result.entry_price)}` : '-'}
                          </div>
                        </td>
                        
                        {/* Stop */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            {result.stop_price ? `$${formatNumber(result.stop_price)}` : '-'}
                          </div>
                        </td>
                        
                        {/* Shares */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {result.shares ? formatNumber(result.shares, 0) : '-'}
                          </div>
                        </td>
                        
                        {/* Risk $ */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-red-600">
                            {result.risk_dollars ? `$${formatNumber(result.risk_dollars, 0)}` : '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Stage3Dashboard;
